// ORCA360 - DOCX Export Utility
// Generates Word documents from transcript data

interface SpeakerSegment {
  speaker_label: string;
  start_time: string;
  end_time: string;
  text: string;
  word_count: number;
  speaker_confidence: number;
}

interface TranscriptData {
  sessionInfo?: {
    trainee_name: string;
    session_date: string;
  };
  transcript_id: string;
  word_count?: number;
  created_at?: string;
  segments: SpeakerSegment[];
}

/**
 * Generate DOCX file from transcript data
 * Uses HTML-based approach for better browser compatibility
 */
export const generateDocx = (data: TranscriptData, logoBase64: string = '') => {
  // Build HTML content
  let html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Session Transcript</title>
  <style>
    body {
      font-family: 'Calibri', 'Arial', sans-serif;
      margin: 0;
      padding: 0;
      line-height: 1.6;
    }
    .header {
      display: flex;
      align-items: center;
      padding: 20px 40px;
      margin: 0;
      background-color: #f5f7fa;
      border-bottom: 3px solid #00DEB3;
      page-break-after: avoid;
    }
    .logo {
      max-width: 100px;
      max-height: 40px;
      margin-right: 15px;
    }
    h1 {
      color: #220BBB;
      font-size: 20pt;
      margin: 0;
      flex: 1;
      font-weight: 600;
    }
    .content {
      padding: 40px;
    }
    .metadata {
      background-color: #f5f7fa;
      padding: 15px;
      margin: 20px 0;
      border-left: 4px solid #00DEB3;
    }
    .metadata p {
      margin: 5px 0;
      font-size: 11pt;
    }
    .segment {
      margin: 20px 0;
      padding: 15px;
      border-left: 4px solid #ccc;
    }
    .segment-header {
      font-weight: bold;
      color: #220BBB;
      margin-bottom: 10px;
    }
    .speaker {
      display: inline-block;
      padding: 3px 8px;
      background-color: #00DEB3;
      color: white;
      border-radius: 3px;
      font-size: 10pt;
      margin-right: 10px;
    }
    .timestamp {
      color: #6E7B85;
      font-size: 10pt;
    }
    .text {
      font-size: 11pt;
      color: #333;
    }
    .footer {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 1px solid #ccc;
      text-align: center;
      color: #666;
      font-size: 9pt;
    }
  </style>
</head>
<body>
  <div class="header">
    ${logoBase64 ? `<img src="${logoBase64}" alt="WingMentor Logo" class="logo">` : ''}
    <h1>WingMentor Session Transcript</h1>
  </div>
  
  <div class="content">
  <div class="metadata">
`;

  if (data.sessionInfo) {
    html += `    <p><strong>Trainee:</strong> ${data.sessionInfo.trainee_name}</p>\n`;
    html += `    <p><strong>Session Date:</strong> ${data.sessionInfo.session_date}</p>\n`;
  }
  
  html += `    <p><strong>Transcript ID:</strong> ${data.transcript_id}</p>\n`;
  html += `    <p><strong>Total Words:</strong> ${data.word_count || 0}</p>\n`;
  html += `    <p><strong>Segments:</strong> ${data.segments.length}</p>\n`;
  
  if (data.created_at) {
    html += `    <p><strong>Created:</strong> ${new Date(data.created_at).toLocaleString()}</p>\n`;
  }
  
  html += `  </div>\n\n`;

  // Add segments
  if (data.segments.length > 0 && data.segments[0]?.speaker_label === 'transcript') {
    // Full text without speaker segments
    html += `  <div class="segment">\n`;
    html += `    <div class="text">${escapeHtml(data.segments[0]?.text || '').replace(/\n/g, '<br>')}</div>\n`;
    html += `  </div>\n`;
  } else {
    // Speaker segments
    data.segments.forEach((segment) => {
      html += `  <div class="segment">\n`;
      html += `    <div class="segment-header">\n`;
      html += `      <span class="speaker">${escapeHtml(segment.speaker_label.toUpperCase().replace('_', ' '))}</span>\n`;
      html += `      <span class="timestamp">[${escapeHtml(segment.start_time)}]</span>\n`;
      
      if (segment.speaker_confidence < 0.8) {
        html += `      <span style="color: #ff9800; font-size: 9pt;"> (${Math.round(segment.speaker_confidence * 100)}% confidence)</span>\n`;
      }
      
      html += `    </div>\n`;
      html += `    <div class="text">${escapeHtml(segment.text)}</div>\n`;
      html += `  </div>\n\n`;
    });
  }

  // Add footer
  html += `  <div class="footer">\n`;
  html += `    <p>Generated by WingMentor - Automated Competency-Based Training Assessment</p>\n`;
  html += `    <p>Document generated on ${new Date().toLocaleString()}</p>\n`;
  html += `  </div>\n`;
  html += `  </div>\n`; // Close .content div
  html += `</body>\n</html>`;

  return html;
};

/**
 * Escape HTML special characters
 */
const escapeHtml = (text: string): string => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/**
 * Download transcript as DOCX
 */
export const downloadTranscriptAsDocx = async (data: TranscriptData) => {
  // Try to fetch and embed the logo as base64
  let logoBase64 = '';
  try {
    const response = await fetch('/images/wingmentor-logo.png');
    if (response.ok) {
      const blob = await response.blob();
      logoBase64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
      console.log('[DOCX Export] Logo embedded successfully');
    }
  } catch (error) {
    console.warn('[DOCX Export] Could not load logo, will use text-only header:', error);
  }
  
  const htmlContent = generateDocx(data, logoBase64);
  
  // Create a blob with proper DOCX MIME type
  // Modern browsers will open this as a Word document
  const blob = new Blob(
    ['\ufeff', htmlContent], // UTF-8 BOM for better compatibility
    { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }
  );
  
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  
  const filename = data.sessionInfo?.trainee_name && data.sessionInfo?.session_date
    ? `transcript_${data.sessionInfo.trainee_name}_${data.sessionInfo.session_date}.doc`
    : `transcript_${data.transcript_id}.doc`;
  
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

